import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldAlert, RefreshCw } from "lucide-react";
import { getPortfolioRisk } from "@/lib/api";

const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: d }));

export const PortfolioRiskCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (force = false) => {
    setBusy(true);
    try { setData(await getPortfolioRisk(force)); } catch { /* endpoint down */ }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const v = data.var95 || {};
  const dd = data.drawdown || {};
  const exp = data.exposureUsd || {};

  return (
    <Card data-testid="portfolio-risk-card" className="border-border/60">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-400" /> Rischio Portafoglio Live
        </CardTitle>
        <button data-testid="portfolio-risk-refresh" onClick={() => load(true)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title="Rileggi l'ultima osservazione disponibile">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p data-testid="portfolio-risk-observation" className={`text-[10px] ${data.stale ? "text-amber-300" : "text-muted-foreground"}`}>
          {data.generatedAt ? `Osservazione: ${new Date(data.generatedAt).toLocaleString("it-IT")}` : "Nessuna osservazione disponibile"}
          {data.stale ? " · dati non aggiornati" : ""}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div data-testid="risk-var">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">VaR 95% 1g</p>
            <p className="font-mono text-lg text-amber-300">
              {fmt(v.var95Usd)}$
              {v.pctOfEquity != null && <span className="text-xs text-muted-foreground"> ({v.pctOfEquity}%)</span>}
            </p>
          </div>
          <div data-testid="risk-exposure">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Esposizione lorda</p>
            <p className="font-mono text-lg">{fmt(data.grossExposureUsd)}$</p>
          </div>
          <div data-testid="risk-drawdown">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Drawdown da picco</p>
            <p className={`font-mono text-lg ${dd.drawdownUsd > 0 ? "text-rose-400" : "text-emerald-400"}`}>
              -{fmt(dd.drawdownUsd)}$
            </p>
          </div>
          <div data-testid="risk-equity">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Equity totale</p>
            <p className="font-mono text-lg">{fmt(data.totalEquityUsd)}$</p>
          </div>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground space-y-1">
          <p data-testid="risk-exposure-detail">
            HL {fmt(exp.hyperliquid)}$ · cTrader {fmt(exp.ctrader)}$ · PM binario (max loss) {fmt(exp.polymarketBinary)}$
          </p>
          {v.maxAbsCorr != null && v.topCorr?.[0] && (
            <p data-testid="risk-corr">
              corr max |{v.topCorr[0].corr}| {v.topCorr[0].a} ↔ {v.topCorr[0].b}
              {v.nProxy > 0 && <span className="text-amber-400/80"> · {v.nProxy} posizioni con vol proxy</span>}
            </p>
          )}
          {v.maxAbsCorr == null && v.nProxy > 0 && (
            <p data-testid="risk-corr" className="text-amber-400/80">{v.nProxy} posizioni con vol proxy (serie storica assente)</p>
          )}
          {(data.notes || []).slice(0, 2).map((n, i) => (
            <p key={i} className="text-muted-foreground/60">{n}</p>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
